/**
 * SprintAssignmentService ドメインサービス
 *
 * スプリント紐付け（SprintProductItem / SprintAssignment）の操作を行う。
 * 純粋関数として実装し、UIフレームワーク（React）に依存しない。
 */

import { SprintDetail } from '../../../lib/functions/sprint'
import { generateUuid } from '../../../utils/uuids'
import {
  SprintProductItem,
  findAssignmentBySprintUuid,
  isAssignedToSprint,
  isAssignedToMultipleSprints,
  isAssignedToAnySprint,
  isRowAssignedToSprint,
  getAssignedSprintUuids,
  findAssignmentByUuid,
} from '../../value-object/SprintProductItem'

// 判定関数を re-export（利便性のため）
export {
  isAssignedToSprint,
  isAssignedToMultipleSprints,
  isAssignedToAnySprint,
  isRowAssignedToSprint,
  getAssignedSprintUuids,
  findAssignmentByUuid,
  findAssignmentBySprintUuid,
}

// ============================================================================
// 操作関数（純粋関数）
// ============================================================================

/**
 * 新しいSprintProductItemを生成する内部ヘルパー
 */
const createNewAssignment = (
  currentAssignments: SprintProductItem[],
  sprintUuid: string,
  displayOrder?: number
): { assignments: SprintProductItem[]; newAssignment: SprintProductItem } => {
  const newAssignment: SprintProductItem = {
    uuid: generateUuid(),
    sprintUuid,
    estimatedHour: 0,
    taskEstimatedHour: 0,
    displayOrder: displayOrder ?? currentAssignments.length,
    lockVersion: 0,
    assignee: undefined,
    sprintGoal: undefined,
  }
  return {
    assignments: [...currentAssignments, newAssignment],
    newAssignment,
  }
}

/**
 * スプリント紐付けを追加する
 *
 * @param currentAssignments 現在の紐付け配列
 * @param sprint 追加するスプリント
 * @param forceNew trueの場合、既存紐付けがあっても新規作成（同一スプリント内複数担当者用）
 * @param displayOrder 新規SPIのdisplayOrder（未指定の場合はcurrentAssignments.lengthを使用）
 * @returns 更新後の紐付け配列と、追加された紐付け（既存の場合は既存のもの）
 */
export const addSprintAssignment = (
  currentAssignments: SprintProductItem[],
  sprint: SprintDetail,
  forceNew = false,
  displayOrder?: number
): { assignments: SprintProductItem[]; newAssignment: SprintProductItem } => {
  const existingAssignment = findAssignmentBySprintUuid(
    currentAssignments,
    sprint.uuid
  )

  // forceNew=trueの場合、または既存紐付けがない場合に新規作成
  if (!existingAssignment || forceNew) {
    return createNewAssignment(currentAssignments, sprint.uuid, displayOrder)
  }

  // 既存の紐付けがある場合はそのまま返す
  return {
    assignments: currentAssignments,
    newAssignment: existingAssignment,
  }
}

/**
 * スプリント未設定のSPIを追加する（常に新規作成）
 *
 * @param currentAssignments 現在の紐付け配列
 * @param unsetSprintUuid UNSET_SPRINT_ROWのUUID（センチネル値）
 * @param displayOrder 新規SPIのdisplayOrder
 * @returns 更新後の紐付け配列と、追加された紐付け
 */
export const addUnsetSprintAssignment = (
  currentAssignments: SprintProductItem[],
  unsetSprintUuid: string,
  displayOrder?: number
): { assignments: SprintProductItem[]; newAssignment: SprintProductItem } => {
  return createNewAssignment(currentAssignments, unsetSprintUuid, displayOrder)
}

/**
 * 指定スプリントの紐付けをすべて削除する
 *
 * @param currentAssignments 現在の紐付け配列
 * @param sprintUuid 削除するスプリントのUUID
 * @returns 更新後の紐付け配列
 */
export const removeSprintAssignments = (
  currentAssignments: SprintProductItem[],
  sprintUuid: string
): SprintProductItem[] => {
  return currentAssignments.filter(a => a.sprintUuid !== sprintUuid)
}

/**
 * 特定のUUIDを持つ紐付けを1つ削除する
 *
 * @param currentAssignments 現在の紐付け配列
 * @param assignmentUuid 削除する紐付けのUUID
 * @returns 更新後の紐付け配列
 */
export const removeSprintAssignmentByUuid = (
  currentAssignments: SprintProductItem[],
  assignmentUuid: string
): SprintProductItem[] => {
  return currentAssignments.filter(a => a.uuid !== assignmentUuid)
}

/**
 * スプリント紐付けを設定する（上書き）
 * 既存の紐付けをすべてクリアして、指定スプリントのみに紐付ける
 *
 * @param sprint 設定するスプリント（undefinedの場合は紐付け解除）
 * @returns 更新後の紐付け配列と、追加された紐付け（undefinedの場合はundefined）
 */
export const setSprintAssignment = (
  sprint: SprintDetail | undefined
): { assignments: SprintProductItem[]; newAssignment?: SprintProductItem } => {
  if (!sprint) {
    return { assignments: [], newAssignment: undefined }
  }

  const newAssignment: SprintProductItem = {
    uuid: generateUuid(),
    sprintUuid: sprint.uuid,
    estimatedHour: 0,
    taskEstimatedHour: 0,
    displayOrder: 0,
    lockVersion: 0,
    assignee: undefined,
    sprintGoal: undefined,
  }

  return {
    assignments: [newAssignment],
    newAssignment,
  }
}
